import { z } from "zod";
import { actorIdentitySchema } from "../protocol/authority.js";
import { acceptedContractEnvelopeSchema, assuranceForContract, contractAmendmentDecisionSchema, contractAmendmentSchema, currentAcceptedContractEnvelopeSchema, } from "../protocol/contract.js";
import { assuranceProjectionSchema } from "../protocol/assurance-projection.js";
import { admittedEvidenceBundleSchema } from "../protocol/evidence-schema.js";
import { UNIVERSAL_VERIFICATION_PROTOCOL, protocolIdSchema, protocolTimestampSchema, protocolVersionSchema, sha256DigestSchema, } from "../protocol/identifiers.js";
import { workAttemptSchema } from "../protocol/attempt.js";
import { currentMachineReadableVerdictReceiptSchema, machineReadableVerdictReceiptSchema, } from "../protocol/verdict.js";
import { enforcementDecisionSchema, enforcementEffectSchema } from "../protocol/enforcement.js";
import { firstUnknownCustomerViewSchema } from "../protocol/first-unknown.js";
import { recurrenceApplicationSchema, recurrenceDriftAssessmentSchema, workflowRecurrenceMatchSchema, } from "../protocol/recurrence.js";
import { reviewRequestSchema, reviewResolutionSchema } from "../protocol/review.js";
import { currentUniversalWorkItemSchema, universalWorkItemSchema } from "../protocol/work.js";
import { universalDogfoodStatusSchema } from "../protocol/canary.js";
import { learningProposalSchema } from "../protocol/learning.js";
import { universalStorageReadIssuesSchema } from "../protocol/stored-projection.js";
import { universalProtocolPolicySchema } from "../protocol/policy.js";
import { completionProposalSchema, completionProposalTraceInputSchema, completionTemplateSchema, } from "../protocol/completion-proposal.js";
import { durableLoopJobSchema } from "../protocol/durable-job.js";
import { operationalDurabilityReportSchema } from "../protocol/operational-durability.js";
import { environmentEnrollmentFixtureMatrixSchema, environmentEnrollmentProviderAdapterHarnessSchema, } from "../protocol/environment-enrollment-fixtures.js";
import { occurrenceBoundarySignalSchema, occurrenceRuntimeObservationSchema, workOccurrenceBoundarySchema, } from "../protocol/occurrence.js";
import { railAdapterKindSchema, railAdapterProfileSchema } from "./adapters.js";
export const universalAdapterRuntimeSchema = z
    .object({
    kind: railAdapterKindSchema,
    delivery: z.enum(["INTENT_ONLY", "LIVE_WEBHOOK"]),
    externalEffectsPossible: z.boolean(),
    liveDeliveryProven: z.boolean(),
    enforcementGuaranteed: z.boolean(),
})
    .strict()
    .superRefine((value, context) => {
    if (value.externalEffectsPossible && value.delivery !== "LIVE_WEBHOOK") {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["externalEffectsPossible"],
            message: "external effects require live webhook delivery",
        });
    }
    if (value.liveDeliveryProven &&
        (value.delivery !== "LIVE_WEBHOOK" || !value.externalEffectsPossible)) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["liveDeliveryProven"],
            message: "live delivery proof requires a live external-effect path",
        });
    }
    if (value.enforcementGuaranteed &&
        (value.delivery !== "LIVE_WEBHOOK" ||
            !value.externalEffectsPossible ||
            !value.liveDeliveryProven)) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["enforcementGuaranteed"],
            message: "an enforcement guarantee requires proven live mediated delivery",
        });
    }
});
export const universalEnvironmentEnrollmentProofSchema = z
    .object({
    fixtureMatrix: environmentEnrollmentFixtureMatrixSchema,
    execution: z.enum(["fixture_matrix_product_visible", "live_adapter_fixture_proven"]),
    browserFixtureExecution: z.enum(["not_claimed", "proven"]),
    providerAdapterHarness: environmentEnrollmentProviderAdapterHarnessSchema,
    providerLifecycle: z
        .object({
        serviceIntegration: z.literal("fixture_only"),
        activeClaim: z.literal("not-claimed"),
        environmentId: z.null(),
        enrollmentReceiptDigest: z.null(),
        activationReceiptDigest: z.null(),
        firstProjectionDigest: z.null(),
    })
        .strict()
        .describe("This overview executes fixture resolution only. Fixture links and coverage digests cannot attest persisted provider enrollment, activation, or first projection. This is not an inventory of stored Environments."),
    identityBoundariesVisible: z.array(protocolIdSchema).min(1),
    unsafeAutoLinksBlocked: z.array(protocolIdSchema).min(1),
})
    .strict()
    .superRefine((value, context) => {
    const scenarios = new Set(value.fixtureMatrix.fixtures.map((fixture) => fixture.scenario));
    for (const required of [
        "provider-identity-collision",
        "auto-link-confusion",
        "unknown-harness",
    ]) {
        if (!scenarios.has(required)) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["fixtureMatrix", "fixtures"],
                message: `environment proof must include unsafe-link scenario ${required}`,
            });
        }
    }
    if (value.browserFixtureExecution === "proven" &&
        value.execution !== "live_adapter_fixture_proven") {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["browserFixtureExecution"],
            message: "browser fixture proof requires a live adapter fixture execution claim",
        });
    }
    if ((value.providerAdapterHarness.externalProviderExecution ===
        "simulated_adapter_harness_proven" ||
        value.providerAdapterHarness.externalProviderExecution ===
            "field_derived_loopback_adapter_proven" ||
        value.providerAdapterHarness.externalProviderExecution ===
            "loopback_http_provider_api_proven") &&
        value.providerAdapterHarness.passedCases !== value.providerAdapterHarness.casesRun) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["providerAdapterHarness"],
            message: "provider adapter harness must pass every external-provider boundary case",
        });
    }
})
    .describe("CU-101 product-visible environment enrollment proof. Fixture coverage is shipped separately from any live/browser execution claim.");
export const universalOccurrenceBoundaryProofSchema = z
    .object({
    runtimeObservations: z.array(occurrenceRuntimeObservationSchema).min(1),
    projectedSignals: z.array(occurrenceBoundarySignalSchema).min(1),
    boundary: workOccurrenceBoundarySchema,
    liveAdapterIngestion: z.enum(["harness_adapter_proven", "production_session_host_pending"]),
    occurrenceTruthSeparated: z.literal(true),
    automaticApplicationAuthority: z.enum(["boundary_only", "requires_accepted_contract"]),
})
    .strict()
    .superRefine((value, context) => {
    if (value.boundary.successfulCompletionEstablished !== false) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["boundary", "successfulCompletionEstablished"],
            message: "occurrence boundary proof must not convert worker completion claims into success",
        });
    }
    if (value.boundary.signalIds.length !== value.projectedSignals.length) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["projectedSignals"],
            message: "product occurrence proof must expose every projected boundary signal",
        });
    }
})
    .describe("CU-102 product-visible occurrence boundary proof. Runtime observations are projected to canonical signals without proving completion truth.");
export const universalTrustMaterialSchema = z
    .object({
    protocol: protocolVersionSchema,
    tenantId: protocolIdSchema,
    keyId: protocolIdSchema,
    algorithm: z.literal("Ed25519"),
    publicKeyPem: z.string().min(1),
    fingerprint: sha256DigestSchema,
    status: z.enum(["active", "retired", "compromised"]),
    createdAt: protocolTimestampSchema,
    retiredAt: protocolTimestampSchema.nullable(),
})
    .strict();
const universalCompletionProposalEngineExampleSchema = z
    .object({
    label: z.string().trim().min(1),
    trace: completionProposalTraceInputSchema,
    proposal: completionProposalSchema,
    productVisibleMeaning: z.string().trim().min(1),
})
    .strict();
export const universalCompletionProposalEngineSchema = z
    .object({
    templates: z.array(completionTemplateSchema).min(1),
    examples: z.array(universalCompletionProposalEngineExampleSchema).min(1),
    activationBoundary: z.literal("proposal_only"),
    transcriptClaimAuthority: z.literal("untrusted_without_independent_oracle"),
})
    .strict()
    .describe("Read-only completion proposal engine projection. It exposes mechanical template selection, abstention, absent trace gaps, alternatives, and transcript-only untrusted claims without activating a Contract.");
const universalAmendmentHistoryEntrySchema = z
    .object({
    proposal: contractAmendmentSchema,
    decision: contractAmendmentDecisionSchema.nullable(),
})
    .strict();
const universalReviewHistoryEntrySchema = z
    .object({
    request: reviewRequestSchema,
    resolution: reviewResolutionSchema.nullable(),
})
    .strict();
export const universalCurrentAuthorityReceiptSchema = currentMachineReadableVerdictReceiptSchema
    .refine((receipt) => receipt.payload.assurance === "PROSPECTIVE", {
    message: "current completion authority requires prospective assurance",
    path: ["payload", "assurance"],
})
    .describe("A current-format receipt with prospective assurance. Retrospective receipts remain readable history only.");
const universalHistoricalLearningProposalListSchema = z
    .array(learningProposalSchema)
    .max(0, "learning proposals are historical candidates, never current completion authority")
    .describe("Always empty in current authority; learning proposals remain readable under incomplete history.");
function currentWorkBindingKey(binding) {
    return `${binding.tenantId}\u0000${binding.workItemId}`;
}
function currentContractBindingKey(binding) {
    return [
        binding.tenantId,
        binding.workItemId,
        binding.contractId,
        binding.contractVersion,
        binding.contractDigest,
    ].join("\u0000");
}
function currentAttemptBindingKey(binding) {
    return [currentContractBindingKey(binding), binding.attemptId, binding.inputSnapshotDigest].join("\u0000");
}
const universalCompletionCurrentHistoryObjectSchema = z
    .object({
    work: z.array(currentUniversalWorkItemSchema),
    contracts: z.array(currentAcceptedContractEnvelopeSchema),
    attempts: z.array(workAttemptSchema),
    receipts: z.array(universalCurrentAuthorityReceiptSchema),
    amendments: z.array(universalAmendmentHistoryEntrySchema),
    reviews: z.array(universalReviewHistoryEntrySchema),
    learningProposals: universalHistoricalLearningProposalListSchema,
})
    .strict();
export const universalCompletionCurrentHistorySchema = universalCompletionCurrentHistoryObjectSchema
    .superRefine((value, context) => {
    const workKeys = new Set(value.work.map((work) => currentWorkBindingKey({ tenantId: work.tenantId, workItemId: work.id })));
    const contracts = new Map(value.contracts.map((contract, index) => {
        if (!workKeys.has(currentWorkBindingKey({
            tenantId: contract.content.tenantId,
            workItemId: contract.content.workItemId,
        }))) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["contracts", index, "content", "workItemId"],
                message: "current Contract requires its exact current Work parent",
            });
        }
        return [
            currentContractBindingKey({
                tenantId: contract.content.tenantId,
                workItemId: contract.content.workItemId,
                contractId: contract.content.contractId,
                contractVersion: contract.content.version,
                contractDigest: contract.contractDigest,
            }),
            contract,
        ];
    }));
    const attempts = new Map(value.attempts.map((attempt, index) => {
        if (!workKeys.has(currentWorkBindingKey(attempt.binding))) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["attempts", index, "binding", "workItemId"],
                message: "current Attempt requires its exact current Work parent",
            });
        }
        if (!contracts.has(currentContractBindingKey(attempt.binding))) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["attempts", index, "binding", "contractDigest"],
                message: "current Attempt requires its exact current Contract parent",
            });
        }
        return [currentAttemptBindingKey(attempt.binding), attempt];
    }));
    value.receipts.forEach((receipt, index) => {
        const binding = receipt.payload.binding;
        const contract = contracts.get(currentContractBindingKey(binding));
        const attempt = attempts.get(currentAttemptBindingKey(binding));
        if (!contract || !attempt) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["receipts", index, "payload", "binding"],
                message: "current Receipt requires its exact current Contract and Attempt parents",
            });
            return;
        }
        if (attempt.state !== "SUBMITTED" ||
            attempt.outputSnapshotDigest !== binding.outputSnapshotDigest) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["receipts", index, "payload", "binding", "outputSnapshotDigest"],
                message: "current Receipt requires the exact submitted Attempt output",
            });
        }
        const derivedAssurance = assuranceForContract(contract, attempt.openedAt);
        if (derivedAssurance !== "PROSPECTIVE" || receipt.payload.assurance !== derivedAssurance) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["receipts", index, "payload", "assurance"],
                message: "current Receipt assurance must be prospectively derived from Contract acceptance and Attempt opening",
            });
        }
    });
})
    .describe("Current completion-aware records with an exact current Work, Contract, Attempt, and Receipt ancestry.");
export const universalCompletionIncompleteHistorySchema = z
    .object({
    work: z.array(universalWorkItemSchema),
    contracts: z.array(acceptedContractEnvelopeSchema),
    attempts: z.array(workAttemptSchema),
    receipts: z.array(machineReadableVerdictReceiptSchema),
    amendments: z.array(universalAmendmentHistoryEntrySchema),
    reviews: z.array(universalReviewHistoryEntrySchema),
    learningProposals: z.array(learningProposalSchema),
})
    .strict()
    .describe("Readable historical or incomplete records. A PASS receipt in this bucket is not current completion authority.");
export const universalCompletionHistoryProjectionSchema = z
    .object({
    current: universalCompletionCurrentHistorySchema,
    incomplete: universalCompletionIncompleteHistorySchema,
})
    .strict();
export const universalEnvironmentBindingProofSchema = z
    .object({
    receiptId: protocolIdSchema,
    workItemId: protocolIdSchema,
    attemptId: protocolIdSchema,
    contractDigest: sha256DigestSchema,
    inputSnapshotDigest: sha256DigestSchema,
    outputSnapshotDigest: sha256DigestSchema,
    signedEnvironmentBindingDigest: sha256DigestSchema,
    replayEnvironmentDigest: sha256DigestSchema,
    currentAuthority: z.boolean(),
    liveCaptureState: z.enum(["current_receipt_bound", "history_only"]),
    requiredCaptureDimensions: z.array(z.enum([
        "contract_snapshot",
        "input_snapshot",
        "output_snapshot",
        "environment_scope",
        "execution_boundary",
        "capability_inventory",
        "dependency_lock",
        "feature_flags",
        "database_migrations",
        "provider_account",
        "configuration",
        "evaluator_versions",
        "replay_environment",
        "secret_custody",
    ])),
    refusalSemantics: z.enum(["missing-or-changed-live-capture-keeps-receipt-ineligible"]),
})
    .strict()
    .superRefine((value, context) => {
    if (value.signedEnvironmentBindingDigest !== value.replayEnvironmentDigest) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["replayEnvironmentDigest"],
            message: "receipt replay must expose the same signed environment binding digest",
        });
    }
    if (value.currentAuthority && value.liveCaptureState !== "current_receipt_bound") {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["liveCaptureState"],
            message: "current authority requires a current receipt-bound environment capture",
        });
    }
})
    .describe("Product projection of CU-202: every visible receipt carries exact contract/input/output snapshots and the signed environment binding digest; changed or missing live capture keeps reuse ineligible.");
const universalIndependentReplayProofSchema = z
    .object({
    evidenceBundleCount: z.number().int().nonnegative(),
    replayCommandCount: z.number().int().nonnegative(),
    independentArtifactCount: z.number().int().nonnegative(),
    supportingArtifactCount: z.number().int().nonnegative(),
    bytesVerifiedArtifactCount: z.number().int().nonnegative(),
    unverifiedArtifactCount: z.number().int().nonnegative(),
    gapReasonCodes: z.array(protocolIdSchema),
    limitationReasonCodes: z.array(protocolIdSchema),
})
    .strict()
    .describe("Product-path summary of CU-201 independent collection and replay evidence. Independent artifacts and replay commands are informational until a signed current receipt authorizes completion.");
/**
 * One admitted artifact, flattened for a console evidence list.
 *
 * The full AdmittedEvidenceBundle stays on the Work detail: it carries inline
 * artifact bytes, and the overview is read on every page. This projection is
 * the identity of the artifact (id, content digest, provenance, admission
 * state) plus the receipts that cite its bundle, which is what an evidence
 * library has to show.
 */
const universalConsoleEvidenceArtifactSchema = z
    .object({
    artifactId: protocolIdSchema,
    bundleDigest: sha256DigestSchema,
    evidenceGraphDigest: sha256DigestSchema,
    workItemId: protocolIdSchema,
    attemptId: protocolIdSchema,
    kind: protocolIdSchema,
    mediaType: z.string().trim().min(1),
    contentDigest: sha256DigestSchema,
    sizeBytes: z.number().int().nonnegative(),
    sourceUri: z.string().trim().min(1),
    collectionMethod: protocolIdSchema,
    collectorActorId: protocolIdSchema,
    observedAt: protocolTimestampSchema,
    admittedAt: protocolTimestampSchema,
    accessibility: z.enum(["accessible", "inaccessible", "redacted", "unknown"]),
    freshness: z.enum(["fresh", "stale", "expired", "unknown"]),
    mutability: z.enum(["immutable", "versioned", "mutable"]),
    independence: z.enum(["independent", "self_attested", "shared_boundary", "unknown"]),
    bytesVerified: z.boolean(),
    citedByReceiptIds: z.array(protocolIdSchema),
})
    .strict();
export const universalConsoleOverviewSourceSchema = z
    .object({
    protocolVersion: protocolVersionSchema,
    generatedAt: protocolTimestampSchema,
    work: z
        .array(universalWorkItemSchema)
        .describe("Full readable Work history supplied to the authoritative projection."),
    contracts: z
        .array(acceptedContractEnvelopeSchema)
        .describe("Full readable Contract history supplied to the authoritative projection."),
    attempts: z
        .array(workAttemptSchema)
        .describe("Full readable Attempt history supplied to the authoritative projection."),
    receipts: z
        .array(machineReadableVerdictReceiptSchema)
        .describe("Full readable Receipt history supplied to the authoritative projection."),
    amendments: z.array(universalAmendmentHistoryEntrySchema),
    reviews: z.array(universalReviewHistoryEntrySchema),
    effects: z
        .array(enforcementEffectSchema)
        .describe("Append-only enforcement-effect history; it is not a completion verdict."),
    learningProposals: z.array(learningProposalSchema),
    // What this build could NOT read out of the tenant's storage. The record
    // arrays above carry only decodable rows, so without this a row that failed
    // to parse would be indistinguishable from a row that was never written.
    storageReadIssues: universalStorageReadIssuesSchema,
    currentActor: actorIdentitySchema,
    principals: z.array(actorIdentitySchema),
    adapters: z.array(railAdapterProfileSchema),
    adapterRuntime: z.array(universalAdapterRuntimeSchema),
    environmentEnrollmentProof: universalEnvironmentEnrollmentProofSchema,
    occurrenceBoundaryProof: universalOccurrenceBoundaryProofSchema,
    trust: universalTrustMaterialSchema,
    trustHistory: z.array(universalTrustMaterialSchema),
    dogfood: universalDogfoodStatusSchema,
    protocolPolicy: universalProtocolPolicySchema,
    protocolPolicyDigest: sha256DigestSchema,
    completionProposalEngine: universalCompletionProposalEngineSchema.optional(),
    durableLoopJobs: z.array(durableLoopJobSchema),
    operationalEvidenceOrigin: z
        .enum(["product_local_store", "synthetic_fixture", "not_observed"])
        .optional(),
    operationalDurability: operationalDurabilityReportSchema,
    independentReplayProof: universalIndependentReplayProofSchema,
    /**
     * The artifacts the bundles counted by independentReplayProof actually
     * hold. Optional so existing overview producers stay valid; informational
     * only — an artifact is never completion authority, a current prospective
     * receipt is.
     */
    evidenceArtifacts: z.array(universalConsoleEvidenceArtifactSchema).optional(),
})
    .strict();
const universalCurrentWorkAliasSchema = universalConsoleOverviewSourceSchema.shape.work.refine((records) => records.every((record) => currentUniversalWorkItemSchema.safeParse(record).success), "top-level Work aliases must contain only current completion-aware records");
const universalCurrentContractAliasSchema = universalConsoleOverviewSourceSchema.shape.contracts.refine((records) => records.every((record) => currentAcceptedContractEnvelopeSchema.safeParse(record).success), "top-level Contract aliases must contain only current completion-aware records");
const universalCurrentReceiptAliasSchema = universalConsoleOverviewSourceSchema.shape.receipts.refine((records) => records.every((record) => universalCurrentAuthorityReceiptSchema.safeParse(record).success), "top-level Receipt aliases require current format and prospective assurance");
const universalConsoleOverviewInferredSchema = universalConsoleOverviewSourceSchema
    .extend({
    work: universalCurrentWorkAliasSchema.describe("Current-only Work compatibility alias; use current for authority and incomplete for history."),
    contracts: universalCurrentContractAliasSchema.describe("Current-only Contract compatibility alias; use current for authority and incomplete for history."),
    attempts: universalCompletionCurrentHistoryObjectSchema.shape.attempts.describe("Current-only Attempt compatibility alias; use current for authority and incomplete for history."),
    receipts: universalCurrentReceiptAliasSchema.describe("Prospective current-only Receipt compatibility alias; retrospective and legacy PASS receipts appear only in incomplete."),
    amendments: universalCompletionCurrentHistoryObjectSchema.shape.amendments,
    reviews: universalCompletionCurrentHistoryObjectSchema.shape.reviews,
    learningProposals: universalCompletionCurrentHistoryObjectSchema.shape.learningProposals,
    current: universalCompletionCurrentHistorySchema,
    incomplete: universalCompletionIncompleteHistorySchema,
    environmentBindingProof: z.array(universalEnvironmentBindingProofSchema),
})
    .strict()
    .superRefine((value, context) => {
    for (const field of [
        "work",
        "contracts",
        "attempts",
        "receipts",
        "amendments",
        "reviews",
        "learningProposals",
    ]) {
        if (JSON.stringify(value[field]) !== JSON.stringify(value.current[field])) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                path: [field],
                message: `${field} must exactly mirror current.${field}`,
            });
        }
    }
})
    .describe("Universal verification status. Only prospective records in current are completion authority; incomplete is readable history and cannot authorize PASS. Learning is historical proposal-only data.");
const _overviewTypeMatchesSchema = true;
void _overviewTypeMatchesSchema;
export const universalConsoleOverviewSchema = universalConsoleOverviewInferredSchema;
const universalWorkDetailHistorySchema = universalCompletionIncompleteHistorySchema
    .omit({ work: true })
    .strict();
const universalWorkDetailCurrentHistorySchema = universalCompletionCurrentHistoryObjectSchema
    .omit({ work: true })
    .strict();
const universalWorkDetailContractBindingSchema = z
    .object({
    contractId: protocolIdSchema,
    contractVersion: z.string().trim().min(1).max(100),
    contractDigest: sha256DigestSchema,
})
    .strict();
export const universalWorkDetailActionAuthoritySchema = z
    .object({
    completionAuthority: z.enum(["CURRENT_PROSPECTIVE", "HISTORICAL_ONLY"]),
    currentContractBindings: z.array(universalWorkDetailContractBindingSchema),
    currentAttemptIds: z.array(protocolIdSchema),
    currentReceiptIds: z.array(protocolIdSchema),
})
    .strict()
    .describe("Action authority derived only from the detail response's exact, ancestry-validated current chain. Historical records never authorize completion.");
const universalWorkDetailEffectSchema = z
    .object({
    receiptId: protocolIdSchema,
    receiptPayloadDigest: sha256DigestSchema,
    decision: enforcementDecisionSchema,
    effect: enforcementEffectSchema,
})
    .strict();
const universalWorkDetailFirstUnknownReviewSchema = z
    .object({
    tenantId: protocolIdSchema,
    workItemId: protocolIdSchema,
    projectId: protocolIdSchema.nullable(),
    processId: protocolIdSchema,
    openedAt: protocolTimestampSchema,
    reasonCodes: z.array(protocolIdSchema).min(1),
    historicalOccurrenceVerification: z.literal("not_run"),
    authority: z.literal("HISTORICAL_ONLY"),
    customerView: firstUnknownCustomerViewSchema,
})
    .strict();
const universalWorkDetailRecurrenceDriftReviewSchema = z
    .object({
    tenantId: protocolIdSchema,
    workItemId: protocolIdSchema,
    processId: protocolIdSchema,
    authority: z.literal("HISTORICAL_ONLY"),
    recurrence: workflowRecurrenceMatchSchema,
    driftAssessment: recurrenceDriftAssessmentSchema,
    application: recurrenceApplicationSchema,
    protectedExecutionActionState: z.enum([
        "accepted_contract_inheritance_available",
        "blocked_until_recurrence_confirmation",
        "blocked_until_successor_acceptance",
        "blocked_until_policy_review",
    ]),
    customerCopy: z
        .object({
        recurrenceLabel: z.enum(["same", "possible", "different"]),
        verdictLabel: z.enum(["eligible", "UNKNOWN"]),
        authorityLabel: z.literal("non-authoritative until accepted"),
        successorLabel: z.enum(["none", "proposed"]),
        protectedExecutionLabel: z.string().trim().min(1),
    })
        .strict(),
})
    .strict();
function addWorkDetailBindingIssue(context, path, kind) {
    context.addIssue({
        code: z.ZodIssueCode.custom,
        path,
        message: `${kind} must be bound to the exact Work and tenant returned by this detail`,
    });
}
function workDetailEvidenceBindingKey(binding) {
    return [
        currentAttemptBindingKey(binding),
        binding.outputSnapshotDigest,
        binding.evidenceGraphDigest,
    ].join("\u0000");
}
export const universalWorkDetailSchema = z
    .object({
    protocolVersion: protocolVersionSchema,
    generatedAt: protocolTimestampSchema,
    work: universalWorkItemSchema,
    current: universalWorkDetailCurrentHistorySchema,
    incomplete: universalWorkDetailHistorySchema,
    evidence: z.array(admittedEvidenceBundleSchema),
    effects: z.array(universalWorkDetailEffectSchema),
    firstUnknownReviews: z.array(universalWorkDetailFirstUnknownReviewSchema),
    recurrenceDriftReviews: z.array(universalWorkDetailRecurrenceDriftReviewSchema),
    actionAuthority: universalWorkDetailActionAuthoritySchema,
})
    .strict()
    .superRefine((value, context) => {
    const workBinding = { tenantId: value.work.tenantId, workItemId: value.work.id };
    const completion = universalCompletionCurrentHistorySchema.safeParse({
        work: currentUniversalWorkItemSchema.safeParse(value.work).success ? [value.work] : [],
        ...value.current,
    });
    if (!completion.success) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["current"],
            message: "current detail records require one exact prospectively valid ancestry chain",
        });
    }
    const buckets = [
        ["current", value.current],
        ["incomplete", value.incomplete],
    ];
    for (const [bucketName, bucket] of buckets) {
        bucket.contracts.forEach((contract, index) => {
            if (contract.content.tenantId !== workBinding.tenantId ||
                contract.content.workItemId !== workBinding.workItemId) {
                addWorkDetailBindingIssue(context, [bucketName, "contracts", index, "content", "workItemId"], "Contract");
            }
        });
        bucket.attempts.forEach((attempt, index) => {
            if (attempt.binding.tenantId !== workBinding.tenantId ||
                attempt.binding.workItemId !== workBinding.workItemId) {
                addWorkDetailBindingIssue(context, [bucketName, "attempts", index, "binding", "workItemId"], "Attempt");
            }
        });
        bucket.receipts.forEach((receipt, index) => {
            if (receipt.payload.binding.tenantId !== workBinding.tenantId ||
                receipt.payload.binding.workItemId !== workBinding.workItemId) {
                addWorkDetailBindingIssue(context, [bucketName, "receipts", index, "payload", "binding", "workItemId"], "Receipt");
            }
        });
        bucket.amendments.forEach(({ proposal, decision }, index) => {
            if (proposal.tenantId !== workBinding.tenantId ||
                proposal.workItemId !== workBinding.workItemId ||
                (decision !== null &&
                    (decision.tenantId !== workBinding.tenantId ||
                        decision.workItemId !== workBinding.workItemId))) {
                addWorkDetailBindingIssue(context, [bucketName, "amendments", index], "Amendment");
            }
        });
        bucket.reviews.forEach(({ request, resolution }, index) => {
            if (request.tenantId !== workBinding.tenantId ||
                request.workItemId !== workBinding.workItemId ||
                (resolution !== null &&
                    (resolution.tenantId !== workBinding.tenantId ||
                        resolution.workItemId !== workBinding.workItemId))) {
                addWorkDetailBindingIssue(context, [bucketName, "reviews", index], "Review");
            }
        });
        bucket.learningProposals.forEach((proposal, index) => {
            if (proposal.tenantId !== workBinding.tenantId ||
                proposal.sourceReceipt.binding.tenantId !== workBinding.tenantId ||
                proposal.sourceReceipt.binding.workItemId !== workBinding.workItemId) {
                addWorkDetailBindingIssue(context, [bucketName, "learningProposals", index, "sourceReceipt", "binding"], "Learning proposal");
            }
        });
    }
    const allReceipts = [...value.current.receipts, ...value.incomplete.receipts];
    const receiptIds = new Set(allReceipts.map((receipt) => receipt.payload.id));
    if (receiptIds.size !== allReceipts.length) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["current", "receipts"],
            message: "returned Receipt IDs must be unique across current and incomplete history",
        });
    }
    const receiptBindingKeys = new Set(allReceipts.map((receipt) => workDetailEvidenceBindingKey(receipt.payload.binding)));
    value.current.receipts.forEach((receipt, receiptIndex) => {
        const exactEvidenceCount = value.evidence.filter((evidence) => workDetailEvidenceBindingKey(evidence.binding) ===
            workDetailEvidenceBindingKey(receipt.payload.binding)).length;
        if (exactEvidenceCount !== 1) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["current", "receipts", receiptIndex, "payload", "binding"],
                message: "every current Receipt requires exactly one admitted Evidence Bundle",
            });
        }
    });
    value.evidence.forEach((evidence, index) => {
        if (!receiptBindingKeys.has(workDetailEvidenceBindingKey(evidence.binding))) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["evidence", index, "binding"],
                message: "evidence must match the exact binding of a returned Receipt",
            });
        }
    });
    const receiptById = new Map(allReceipts.map((receipt) => [receipt.payload.id, receipt]));
    value.effects.forEach(({ receiptId, receiptPayloadDigest, decision, effect }, index) => {
        const receipt = receiptById.get(receiptId);
        if (!receiptIds.has(receiptId) ||
            !receipt ||
            receipt.payloadDigest !== receiptPayloadDigest ||
            decision.tenantId !== workBinding.tenantId ||
            decision.receiptId !== receiptId ||
            decision.receiptPayloadDigest !== receiptPayloadDigest ||
            workDetailEvidenceBindingKey(decision.binding) !==
                workDetailEvidenceBindingKey(receipt.payload.binding) ||
            effect.tenantId !== workBinding.tenantId ||
            effect.decisionId !== decision.id) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["effects", index],
                message: "effect must match the exact returned Receipt payload, durable Decision, and tenant",
            });
        }
    });
    value.firstUnknownReviews.forEach((review, index) => {
        if (review.tenantId !== workBinding.tenantId ||
            review.workItemId !== workBinding.workItemId ||
            review.customerView.statusLabel !== "UNKNOWN" ||
            review.customerView.eligibilityLabel !== "not yet eligible" ||
            review.customerView.canonicalVerdictHidden !== true ||
            review.customerView.workflowTextareaRequired !== false ||
            review.authority !== "HISTORICAL_ONLY") {
            addWorkDetailBindingIssue(context, ["firstUnknownReviews", index], "First UNKNOWN review");
        }
    });
    value.recurrenceDriftReviews.forEach((review, index) => {
        if (review.tenantId !== workBinding.tenantId ||
            review.workItemId !== workBinding.workItemId ||
            review.recurrence.occurrenceId !== workBinding.workItemId ||
            review.application.occurrenceId !== workBinding.workItemId ||
            review.processId !== review.recurrence.processId ||
            review.processId !== review.driftAssessment.processId ||
            review.driftAssessment.id !== review.application.driftAssessmentId ||
            review.authority !== "HISTORICAL_ONLY" ||
            review.customerCopy.authorityLabel !== "non-authoritative until accepted") {
            addWorkDetailBindingIssue(context, ["recurrenceDriftReviews", index], "Recurrence drift review");
        }
        if (review.recurrence.relationship !== "same" &&
            (review.recurrence.attachedContractDigest !== null ||
                review.recurrence.protectedContractInheritanceAllowed)) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["recurrenceDriftReviews", index, "recurrence"],
                message: "possible and different recurrence matches cannot inherit accepted contracts before confirmation",
            });
        }
        if (review.application.successorProposal && review.application.protectedExecutionAllowed) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["recurrenceDriftReviews", index, "application", "protectedExecutionAllowed"],
                message: "successor proposals must stay blocked before accepted successor authority",
            });
        }
        if (review.application.state === "REQUIRES_POLICY_REVIEW" &&
            review.protectedExecutionActionState !== "blocked_until_policy_review") {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["recurrenceDriftReviews", index, "protectedExecutionActionState"],
                message: "consequence drift must route protected execution through policy review",
            });
        }
        if (review.driftAssessment.relationship === "material_drift" &&
            (review.application.canonicalVerdictState !== "UNKNOWN" ||
                review.application.successorProposal === null)) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["recurrenceDriftReviews", index, "application"],
                message: "material recurrence drift must return to UNKNOWN confirmation with a successor proposal",
            });
        }
    });
    const expectedContractBindings = value.current.contracts.map((contract) => ({
        contractId: contract.content.contractId,
        contractVersion: contract.content.version,
        contractDigest: contract.contractDigest,
    }));
    const expectedAttemptIds = value.current.attempts.map((attempt) => attempt.binding.attemptId);
    const expectedReceiptIds = value.current.receipts.map((receipt) => receipt.payload.id);
    const expectedCompletionAuthority = expectedReceiptIds.length > 0 ? "CURRENT_PROSPECTIVE" : "HISTORICAL_ONLY";
    for (const [field, expected] of [
        ["currentContractBindings", expectedContractBindings],
        ["currentAttemptIds", expectedAttemptIds],
        ["currentReceiptIds", expectedReceiptIds],
    ]) {
        if (JSON.stringify(value.actionAuthority[field]) !== JSON.stringify(expected)) {
            context.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["actionAuthority", field],
                message: `${field} must exactly mirror the ancestry-validated current bucket`,
            });
        }
    }
    if (value.actionAuthority.completionAuthority !== expectedCompletionAuthority) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["actionAuthority", "completionAuthority"],
            message: "completion authority requires at least one current prospective Receipt",
        });
    }
})
    .describe("Canonical tenant-bound universal Work detail. Current ancestry and action authority are separate from readable incomplete history.");
export const universalReceiptDetailSchema = z
    .object({
    protocolVersion: protocolVersionSchema,
    receipt: machineReadableVerdictReceiptSchema,
    assurance: assuranceProjectionSchema,
    evidence: admittedEvidenceBundleSchema,
    contract: acceptedContractEnvelopeSchema.nullable(),
    attempt: workAttemptSchema.nullable(),
})
    .strict()
    .superRefine((value, context) => {
    if (value.assurance.id !== `assurance:${value.receipt.payload.id}`) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["assurance", "id"],
            message: "receipt detail assurance must identify the returned receipt",
        });
    }
    if (value.assurance.signedReceiptExists !== true) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["assurance", "signedReceiptExists"],
            message: "receipt detail transport must be based on a signed receipt envelope",
        });
    }
    if (value.assurance.currentReceiptEligible &&
        value.assurance.displayLabel !== "verified completion") {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["assurance", "displayLabel"],
            message: "eligible receipt detail assurance must use verified completion copy",
        });
    }
    if (!value.assurance.currentReceiptEligible &&
        value.assurance.displayLabel.includes("verified")) {
        context.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["assurance", "displayLabel"],
            message: "ineligible receipt detail assurance cannot use verified completion copy",
        });
    }
});
export function createUniversalReceiptDetail(input) {
    return universalReceiptDetailSchema.parse({
        protocolVersion: UNIVERSAL_VERIFICATION_PROTOCOL,
        ...input,
    });
}
